import { button, el } from './dom';

export function usesTouchControls(): boolean {
  return navigator.maxTouchPoints > 0 || window.matchMedia?.('(pointer: coarse), (max-width: 700px)').matches === true;
}

export interface TouchControlHooks {
  onMove: (x: number, z: number) => void;
  onPitch: (direction: number) => void;
  onAction: (action: 'interact' | 'notebook' | 'terminal' | 'scanner' | 'monitor' | 'menu') => void;
}

/** The central pad controls forward/back movement and continuous turning. */
export class TouchControls {
  readonly root: HTMLElement;
  private stick = el('div', { class: 'touch-stick', role: 'group', 'aria-label': '移動・旋回パッド' });
  private thumb = el('div', { class: 'touch-thumb' });
  private moveId: number | null = null;
  private pitchPointer: { node: HTMLElement; id: number } | null = null;

  constructor(private readonly hooks: TouchControlHooks) {
    this.stick.append(this.thumb, el('span', { class: 'touch-stick-label', text: '上下で移動・左右で旋回' }));
    const actions: [string, Parameters<TouchControlHooks['onAction']>[0]][] = [
      ['手帳', 'notebook'], ['端末', 'terminal'], ['電波', 'scanner'], ['通信', 'monitor'], ['メニュー', 'menu'],
    ];
    this.root = el('div', { class: 'touch-controls', hidden: true }, [
      this.stick,
      el('div', { class: 'touch-pitch', 'aria-label': '上下の視点' }, [
        this.pitchButton('上を見る', 1),
        this.pitchButton('下を見る', -1),
      ]),
      button('調べる・話す', () => hooks.onAction('interact'), 'btn touch-interact'),
      el('nav', { class: 'touch-toolbar', 'aria-label': 'ゲームの道具' },
        actions.map(([label, action]) => button(label, () => hooks.onAction(action), 'btn'))),
    ]);

    this.stick.addEventListener('pointerdown', event => {
      if (this.root.hidden || this.moveId !== null) return;
      event.preventDefault();
      this.moveId = event.pointerId;
      this.stick.setPointerCapture(event.pointerId);
      this.move(event);
    });
    this.stick.addEventListener('pointermove', event => {
      if (event.pointerId === this.moveId) this.move(event);
    });
    const stopMove = (event: PointerEvent): void => {
      if (event.pointerId !== this.moveId) return;
      this.moveId = null;
      this.thumb.style.transform = '';
      hooks.onMove(0, 0);
    };
    for (const name of ['pointerup', 'pointercancel', 'lostpointercapture']) {
      this.stick.addEventListener(name, event => stopMove(event as PointerEvent));
    }
  }

  private pitchButton(label: string, direction: number): HTMLButtonElement {
    const node = el('button', { type: 'button', class: 'btn touch-pitch-button', text: label });
    node.addEventListener('pointerdown', event => {
      if (this.root.hidden || this.pitchPointer !== null) return;
      event.preventDefault();
      this.pitchPointer = { node, id: event.pointerId };
      node.setPointerCapture(event.pointerId);
      this.hooks.onPitch(direction);
    });
    for (const name of ['pointerup', 'pointercancel', 'lostpointercapture']) {
      node.addEventListener(name, event => {
        if (this.pitchPointer?.node !== node || this.pitchPointer.id !== (event as PointerEvent).pointerId) return;
        this.pitchPointer = null;
        this.hooks.onPitch(0);
      });
    }
    return node;
  }

  private move(event: PointerEvent): void {
    const rect = this.stick.getBoundingClientRect();
    const radius = rect.width * 0.32;
    const x = (event.clientX - rect.left - rect.width / 2) / radius;
    const z = (event.clientY - rect.top - rect.height / 2) / radius;
    const length = Math.hypot(x, z);
    const scale = Math.max(1, length);
    const nx = length < 0.12 ? 0 : x / scale;
    const nz = length < 0.12 ? 0 : z / scale;
    this.thumb.style.transform = `translate(${nx * radius}px, ${nz * radius}px)`;
    this.hooks.onMove(nx, nz);
  }

  setEnabled(enabled: boolean): void {
    if (this.root.hidden === !enabled) return;
    this.root.hidden = !enabled;
    if (!enabled) this.reset();
  }

  reset(): void {
    const moveId = this.moveId;
    const pitchPointer = this.pitchPointer;
    this.moveId = null;
    this.pitchPointer = null;
    if (moveId !== null && this.stick.hasPointerCapture(moveId)) this.stick.releasePointerCapture(moveId);
    if (pitchPointer?.node.hasPointerCapture(pitchPointer.id)) pitchPointer.node.releasePointerCapture(pitchPointer.id);
    this.thumb.style.transform = '';
    this.hooks.onMove(0, 0);
    this.hooks.onPitch(0);
  }
}
